/**
 * Die zehn Bahnfarben und die Tonleiter (STATIONEN-BAHNEN.md 2.7 und 2.6).
 *
 * Die Werte stehen doppelt: hier als Zahlen, in `stil/tokens.css` als
 * Tokens `--eh-bahn-{id}-akz|dunkel|tint`. Das ist Absicht – die Tonleiter
 * mischt zwischen zwei Farben, und dafür braucht es die Werte im Code, nicht
 * nur im Stylesheet. Ändert sich eine Farbe, ändert sie sich an beiden
 * Stellen; die Abnahme prüft es.
 */

export interface Bahnfarbe {
  id: string;
  name: string;
  akz: string;
  dunkel: string;
  tint: string;
}

export const BAHNFARBEN: Bahnfarbe[] = [
  { id: 'warmgrau', name: 'Warmgrau', akz: '#9A928B', dunkel: '#5C574F', tint: '#F7F5F2' },
  { id: 'taupe', name: 'Taupe', akz: '#8B7B6E', dunkel: '#6F6154', tint: '#F1EDE9' },
  { id: 'erdbraun', name: 'Erdbraun', akz: '#746E65', dunkel: '#4A443F', tint: '#EEEAE6' },
  { id: 'kupfer', name: 'Kupfer', akz: '#A0674A', dunkel: '#7E4E36', tint: '#F5ECE6' },
  { id: 'pflaume', name: 'Pflaume', akz: '#7D6479', dunkel: '#5F4A5C', tint: '#F1EAF0' },
  { id: 'olive', name: 'Deep Olive', akz: '#5E6C60', dunkel: '#4A564C', tint: '#EAEDEA' },
  { id: 'salbei', name: 'Salbei', akz: '#60705A', dunkel: '#4A5745', tint: '#EAF0E7' },
  { id: 'steingrau', name: 'Steingrau', akz: '#6F7772', dunkel: '#565D59', tint: '#EDEFEE' },
  { id: 'slate', name: 'Info-Slate', akz: '#667B82', dunkel: '#4F6268', tint: '#EAF0F1' },
  { id: 'nachtblau', name: 'Nachtblau', akz: '#5A6B8C', dunkel: '#44536F', tint: '#E9EDF4' },
];

/** Die Partei, der eine Bahn gehört. */
export type BahnId = 'eh' | 'lief' | 'kunde';

/** Vorgabe: Eichenherz Warmgrau · Lieferant Deep Olive · Kunde Info-Slate (2.7). */
export const BAHNEN_VORGABE: Record<BahnId, string> = {
  eh: 'warmgrau',
  lief: 'olive',
  kunde: 'slate',
};

export function farbeVon(id: string): Bahnfarbe {
  return BAHNFARBEN.find((f) => f.id === id) ?? BAHNFARBEN[0]!;
}

function zahlen(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/**
 * Die Tonleiter innerhalb einer Bahn (2.6).
 *
 * Linear gemischt zwischen `akz` beim ersten und `dunkel` beim letzten
 * Schritt. Bei einem einzigen Schritt bleibt es bei `akz` – eine Leiter mit
 * einer Sprosse ist keine.
 */
export function tonleiter(farbe: Bahnfarbe, anzahl: number): string[] {
  if (anzahl <= 1) return [farbe.akz];
  const a = zahlen(farbe.akz);
  const d = zahlen(farbe.dunkel);
  return Array.from({ length: anzahl }, (_, i) => {
    const t = i / (anzahl - 1);
    const teil = a.map((wert, k) => Math.round(wert + (d[k]! - wert) * t));
    return `#${teil.map((x) => x.toString(16).padStart(2, '0')).join('')}`;
  });
}

/**
 * Die gespeicherte Wahl lesen – und dabei aufräumen.
 *
 * Unbekannte Ids (eine Farbe ist aus der Palette gefallen) und
 * Doppelbelegungen werden verworfen; für beides gilt dann die Vorgabe.
 * Zwei Bahnen dürfen nie dieselbe Farbe tragen (2.7).
 */
export function bahnenfarbenLesen(roh: string | null | undefined): Record<BahnId, string> {
  const wahl: Record<BahnId, string> = { ...BAHNEN_VORGABE };
  if (roh) {
    try {
      const gelesen = JSON.parse(roh) as Partial<Record<BahnId, string>>;
      for (const id of ['eh', 'lief', 'kunde'] as BahnId[]) {
        const f = gelesen[id];
        if (f && BAHNFARBEN.some((x) => x.id === f)) wahl[id] = f;
      }
    } catch {
      /* Kaputter Eintrag: dann eben die Vorgabe. */
    }
  }
  /* Doppelbelegung auflösen – die spätere Bahn weicht auf eine freie aus. */
  const belegt = new Set<string>();
  for (const id of ['eh', 'lief', 'kunde'] as BahnId[]) {
    if (!belegt.has(wahl[id])) {
      belegt.add(wahl[id]);
      continue;
    }
    const frei = BAHNFARBEN.find((f) => !belegt.has(f.id));
    wahl[id] = frei ? frei.id : wahl[id];
    belegt.add(wahl[id]);
  }
  return wahl;
}
